import { z } from 'zod'

import { addDays, type CalendarDay } from '@/shared/ui/calendar'

import { classStatuses } from '../class-form/class'
import { daysCovered, type CalendarSession } from './calendar'

/** An instructor's own calendar (CLS-25), by their User Profile's public id. */
export const instructorMonthRoute = '/classes/instructors/:publicId/calendar'

export function instructorMonthPath(userProfileId: string): string {
  return `/classes/instructors/${encodeURIComponent(userProfileId)}/calendar`
}

/** The site-wide Instructor Schedule (CLS-25). */
export const instructorSchedulePath = '/classes/instructor-schedule'

/** The API's calendar of one instructor, from `from` to `to` (`YYYY-MM-DD`, at most 42 days) in `timeZone`. */
export function instructorMonthApi(userProfileId: string, range: { readonly from: string; readonly to: string }, timeZone: string): string {
  return `/classes/v1/instructors/${encodeURIComponent(userProfileId)}/calendar?${new URLSearchParams({ ...range, timeZone }).toString()}`
}

/** What the Instructor Schedule looks for: a range of days and the start of a last and a first name. */
export interface ScheduleSearch {
  readonly from: CalendarDay
  readonly to: CalendarDay
  readonly lastName: string
  readonly firstName: string
  readonly isDescending: boolean
}

/** The API's Instructor Schedule, one page at a time. */
export function instructorScheduleApi(search: ScheduleSearch, timeZone: string, cursor: string | null): string {
  const query = new URLSearchParams({ from: search.from, to: search.to, timeZone })
  if (search.lastName.trim() !== '') {
    query.set('lastName', search.lastName.trim())
  }
  if (search.firstName.trim() !== '') {
    query.set('firstName', search.firstName.trim())
  }
  if (search.isDescending) {
    query.set('sort', '-name')
  }
  if (cursor !== null) {
    query.set('cursor', cursor)
  }
  return `/classes/v1/instructor-schedule?${query.toString()}`
}

const sessionSchema = z.object({
  sessionId: z.string(),
  classId: z.string(),
  className: z.string(),
  organizationId: z.string(),
  organizationName: z.string().nullish(),
  // False for an owner on another site, whose details do not open on this one.
  isOrganizationOnThisSite: z.boolean(),
  status: z.enum(classStatuses),
  // A color Catalog adds later shows in the default blue rather than breaking the page.
  calendarColor: z.string(),
  startsOn: z.string(),
  endsOn: z.string(),
})

/** A session an instructor teaches. */
export type InstructorSession = z.infer<typeof sessionSchema>

const blockoutSchema = z.object({ blockoutId: z.string(), name: z.string(), startsOn: z.string(), endsOn: z.string() })

/** A time an instructor cannot teach. */
export type InstructorBlockout = z.infer<typeof blockoutSchema>

const daysSchema = z.object({
  userProfileId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  // Null when they have no name: never an email address.
  displayName: z.string().nullable(),
  isContractInstructor: z.boolean(),
  isOnThisSite: z.boolean(),
  timeZone: z.string(),
  from: z.string(),
  to: z.string(),
  sessions: z.array(sessionSchema),
  // Null when the viewer may not read the instructor's blockout times.
  blockouts: z.array(blockoutSchema).nullable(),
})

/** The instructor's name as the schedule lists it, "Last, First", or `null` when they have neither. */
export function listedName(days: Pick<InstructorDays, 'firstName' | 'lastName'>): string | null {
  const parts = [days.lastName.trim(), days.firstName.trim()].filter((part) => part !== '')
  return parts.length === 0 ? null : parts.join(', ')
}

/** One instructor over a range of days: the sessions they teach and their blockout times. */
export type InstructorDays = z.infer<typeof daysSchema>

export function parseInstructorDays(body: unknown): InstructorDays {
  return daysSchema.parse(body)
}

const pageSchema = z.object({
  data: z.array(daysSchema),
  pagination: z.object({ nextCursor: z.string().nullish() }),
})

/** One page of the Instructor Schedule. */
export function parseSchedulePage(body: unknown): { readonly rows: readonly InstructorDays[]; readonly nextCursor: string | null } {
  const page = pageSchema.parse(body)
  return { rows: page.data, nextCursor: page.pagination.nextCursor ?? null }
}

/** Anything with a start and an end, as the day grids place sessions and blockouts. */
export type Span = Pick<CalendarSession, 'startsOn' | 'endsOn'>

/**
 * The spans on each day from `from` to `to` in `timeZone`, each in their order: worked out once
 * per span, so a grid's cells only look their day up.
 */
export function spansByDay<T extends Span>(spans: readonly T[], timeZone: string, from: CalendarDay, to: CalendarDay): ReadonlyMap<CalendarDay, readonly T[]> {
  const byDay = new Map<CalendarDay, T[]>()
  for (const span of spans) {
    const { first, last } = daysCovered(span, timeZone)
    const end = last < to ? last : to
    // Stops on the last day itself: past 9999-12-31 the next day would not compare as later.
    for (let day = first < from ? from : first; day <= end; day = addDays(day, 1)) {
      const listed = byDay.get(day)
      if (listed === undefined) {
        byDay.set(day, [span])
      } else {
        listed.push(span)
      }
      if (day === end) {
        break
      }
    }
  }
  return byDay
}

/** The days from `from` to `to`, counting both. */
export function daysBetween(from: CalendarDay, to: CalendarDay): readonly CalendarDay[] {
  const days: CalendarDay[] = []
  // Stops on the last day itself: past 9999-12-31 the next day would not compare as later.
  for (let day = from; day <= to; day = addDays(day, 1)) {
    days.push(day)
    if (day === to) {
      break
    }
  }
  return days
}
